import React, { useEffect, useRef, useMemo, memo, useState } from "react";
import GoogleMapReact from 'google-map-react';
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { defaultMapProps, mapApiKey, getStillageTypeData } from "services/stillageDashboard";
import PropTypes from 'prop-types';
import MapWrapper from "./MapWrapper";
import { Row, Col, FormControl, InputGroup, Badge, Stack } from "react-bootstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { capitalize, themeColors } from "helpers/utils";
import classNames from "classnames";

const ICON_COLOURS = {
    full: themeColors.success,
    low: themeColors.warning,
    empty: themeColors.danger,
    scanned: themeColors.primary
};

const StillageMap = ({ stillages }) => {
    const stils = useMemo(() => getStillageTypeData(stillages), [stillages]);
    const [data, setData] = useState(stils);
    const [activeFilters, setActiveFilters] = useState(new Set());

    useEffect(() => setData(stils), [stils]);

    // Store map and maps objects
    const mapRef = useRef();
    const mapsRef = useRef();
    const markersRef = useRef([]);
    const markerClustererRef = useRef(null);

    // Filter map icons by key
    const handleLegendClick = key => {
        setActiveFilters(filters => {
            let set = filters;
            if (filters.has(key))
                set.delete(key);
            else
                set.add(key);

            return new Set([...set]);
        });

        setData(() => {
            if (activeFilters.size === 0 || activeFilters.size === Object.keys(ICON_COLOURS).length)
                return stils;

            let data = [];
            if (activeFilters.has('full'))
                data = [...data, ...stils.filter(x => x.battery > 50)];
            if (activeFilters.has('low'))
                data = [...data, ...stils.filter(x => x.battery > 0 && x.battery <= 50)];
            if (activeFilters.has('empty'))
                data = [...data, ...stils.filter(x => x.battery === 0 && !x.useScanLocation)];
            if (activeFilters.has('scanned'))
                data = [...data, ...stils.filter(x => x.useScanLocation)];
            return data;
        });
    }

    // When Google Maps API is loaded, store map and maps
    const handleApiLoaded = ({ map, maps }) => {
        mapRef.current = map;
        mapsRef.current = maps;
    };

    // Update markers/clusters when stillages change
    useEffect(() => {
        const map = mapRef.current;
        const maps = mapsRef.current;
        if (!map || !maps || !Array.isArray(stils)) return;

        markersRef.current.forEach(marker => marker.setMap(null));
        markersRef.current = [];
        markerClustererRef.current?.clearMarkers();

        // Create new markers
        const markers = data.map(stillage => {
            let colour = ICON_COLOURS.full;
            if (stillage.useScanLocation) colour = ICON_COLOURS.scanned;
            else if (stillage.battery === 0) colour = ICON_COLOURS.empty;
            else if (stillage.battery <= 50) colour = ICON_COLOURS.low;

            const svg = `<?xml version="1.0" encoding="UTF-8"?><svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 384 512'><path fill='${colour}' d='M192 0C86 0 0 86 0 192c0 77.4 27.6 99.7 172.7 310.3a24 24 0 0 0 38.6 0C356.4 291.7 384 269.4 384 192 384 86 298 0 192 0zm0 272a80 80 0 1 1 0-160 80 80 0 0 1 0 160z'/></svg>`;
            const svgUrl = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;

            const marker = new maps.Marker({
                position: { lat: stillage.lat, lng: stillage.long },
                icon: {
                    url: svgUrl,
                    scaledSize: { width: 40, height: 40 }
                }
            });

            // InfoWindow content
            const infoContent = `
                <table class="table table-borderless table-sm table-condensed" style="margin-bottom:0; margin-top:0;">
                    <tbody>
                    <tr><th>Stillage No</th><td>${stillage.stillageNo}</td></tr>
                    <tr><th>Stillage Type</th><td>${stillage.stillageType.label}</td></tr>
                    <tr><th>Battery</th><td>${stillage.battery ?? ""}</td></tr>
                    <tr><th>Device Name</th><td>${stillage.trackerName ?? ""}</td></tr>
                    <tr><th>Cust Name</th><td>${stillage.custName ?? 'N/A'}</td></tr>
                    <tr><th>Last Poll</th><td>${stillage.lastPoll ? stillage.lastPoll : ""}</td></tr>
                    </tbody>
                </table>`;
            const infoWindow = new maps.InfoWindow({
                content: infoContent,
                disableAutoPan: true
            });

            marker.addListener("mouseover", () => {
                infoWindow.open(map, marker);
            });
            marker.addListener("mouseout", () => {
                infoWindow.close();
            });

            return marker;
        });

        markerClustererRef.current = new MarkerClusterer({ map, markers });

        // Save markers for cleanup
        markersRef.current = markers;
    }, [data, stils, stillages, mapRef, mapsRef]);

    return (
        // Important! Always set the container height explicitly
        <>
            <Row>
                <Col xs={6}>
                    <InputGroup className="mt-2 mb-2">
                        <FormControl
                            onBlur={({ target: { value } }) => {
                                setData(value ? stils.filter(x => x.stillageNo == value) : stils)
                            }}
                            size="sm"
                            id="map-search"
                            type="search"
                            className="shadow-none"
                            placeholder="Filter by stillage No."
                        />
                        <InputGroup.Text className="bg-transparent cursor-pointer">
                            <FontAwesomeIcon icon="search" className="fs--1 text-600" />
                        </InputGroup.Text>
                    </InputGroup>
                </Col>
                <Col xs={6} className="d-flex justify-content-end mt-2 mb-2">
                    <Stack direction="horizontal">
                        <small className="fw-bold">Key: </small>
                        {Object.keys(ICON_COLOURS).map(x => {
                            return (
                                <Badge
                                    key={x}
                                    title={`Show/ Hide ${x}`}
                                    className={classNames('me-2 cursor-pointer', { 'border border-dark shadow': activeFilters.has(x) })}
                                    bg="none"
                                    pill
                                    style={{ backgroundColor: ICON_COLOURS[x] }}
                                    onClick={() => handleLegendClick(x)}
                                >{capitalize(x)}</Badge>
                            )
                        })}
                    </Stack>
                </Col>
            </Row>
            <MapWrapper height={'400px'} width={'100%'}>
                <GoogleMapReact
                    bootstrapURLKeys={{ key: mapApiKey }}
                    defaultCenter={defaultMapProps.center}
                    defaultZoom={defaultMapProps.zoom}
                    yesIWantToUseGoogleMapApiInternals
                    onGoogleApiLoaded={handleApiLoaded}
                >
                </GoogleMapReact>
            </MapWrapper>
        </>
    );
}

StillageMap.propTypes = {
    stillages: PropTypes.array
};

export default memo(StillageMap);